import React, { useState, useEffect } from "react";
import DnDFileUpload from "../../../components/MDDForm/components/DnDFileUpload.jsx";
import { Box, Button, Modal, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../../../hooks/user.js";
import { useBreadcrumb } from "../../../contexts/BreadcrumbContext.jsx";
import PaymentImportsTable from "../../../components/tables/PaymentImportsTable.jsx";

import { usePaymentImportActions } from "../../../hooks/usePaymentImportActions.jsx";

const breadcrumbs = [
  { name: "Home", path: "/home" },
  {
    name: "Buchungsverwaltung",
    path: "/admin/buchungen",
  },
  {
    name: "Zahlungsimporte",
    path: "/admin/buchungen/imports",
  },
];

const PaymentImportIndexPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsPrefix = "pi";

  const [tableConfig, setTableConfig] = useState(null);

  useEffect(() => {
    const pageIndex = Number(searchParams.get(`${searchParamsPrefix}.pageIndex`));
    const searchword = searchParams.get(`${searchParamsPrefix}.searchword`);
    setTableConfig({ pageIndex, searchword });
  }, []);

  // User
  const { user } = useUser();

  // Breadcrumbs
  const { setBreadcrumbs } = useBreadcrumb();

  // Snackbar
  const { enqueueSnackbar } = useSnackbar();

  // Navigation
  const navigate = useNavigate();

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, [setBreadcrumbs]);

  const [loading, setLoading] = useState(true);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { createPaymentImport } = usePaymentImportActions();

  // Datei-Import-Funktionen
  const handleOpenImportModal = () => {
    setImportModalOpen(true);
    setSelectedFiles([]);
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    setSelectedFiles([]);
  };

  const handleImportSubmit = async () => {
    if (!selectedFiles.length) {
      enqueueSnackbar("Keine Datei ausgewählt.", { variant: "warning" });
      return;
    }

    const formData = new FormData();

    const file = selectedFiles[0];

    if (file.filedata && file.filename) {
      // Extrahiere Base64-Daten
      const base64Data = file.filedata.split(",")[1];
      const binaryData = atob(base64Data);
      const byteArray = new Uint8Array(binaryData.length);

      for (let i = 0; i < binaryData.length; i++) {
        byteArray[i] = binaryData.charCodeAt(i);
      }

      // Erstelle Blob und File-Objekt
      const blob = new Blob([byteArray], {
        type: file.mimetype || "application/octet-stream",
      });
      const fileObject = new File([blob], file.filename, {
        type: file.mimetype || "application/octet-stream",
      });

      formData.append("file", fileObject);
    } else {
      console.error("Ungültiges Dateiobjekt:", file);
      enqueueSnackbar("Ungültige Datei ausgewählt.", { variant: "error" });
      return;
    }

    await createPaymentImport(formData, {
      setLoading,
      setResult: () => {
        enqueueSnackbar("Zahlungen erfolgreich importiert.", {
          variant: "success",
        });
        setImportModalOpen(false);
        setSelectedFiles([]);
      },
    });

    // reset selected files
    setSelectedFiles([]);
    // close modal
    setImportModalOpen(false);
  };

  const onChangeTableState = (state) => {
    setSearchParams(
      (currentParams) => {
        const params = new URLSearchParams(currentParams);
        params.set(`${searchParamsPrefix}.pageIndex`, state.pageIndex);
        if(state.searchword) {
          params.set(`${searchParamsPrefix}.searchword`, state.searchword);
        }
        return params;
      },
      { preventScrollReset: true }
    );
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2">
          Übersicht aller Buchungsimporte vom Zahlungsdienstleister (Starmoney).
          Nach dem Import einer Zahlungsdatei werden die eingegangenen Zahlungen
          den jeweiligen Schäden zugeordnet. Ausgehende Zahlungen dienen dazu,
          Ausgangsbuchungen als abgeschlossen zu markieren.
        </Typography>
      </Box>

      {tableConfig && (
        <PaymentImportsTable
          onRowClick={(payment_import) => {
            navigate(`/admin/buchungen/imports/${payment_import.id}`);
          }}
          onClickUpload={handleOpenImportModal}
          onChangeTableState={onChangeTableState}
          tableConfig={tableConfig}
        />
      )}

      {/* Import Modal */}
      <Modal
        open={importModalOpen}
        onClose={handleCloseImportModal}
        aria-labelledby="import-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" id="import-modal-title" className="mb-4">
            Zahlungen importieren
          </Typography>

          <DnDFileUpload
            upload={selectedFiles}
            setUpload={setSelectedFiles}
            multiple={false}
            add_description={false}
            allowedExtensions={["CSV", "sta", "XML"]}
            disabled={false}
          />

          <div className="flex justify-end gap-2">
            <Button onClick={handleCloseImportModal} variant="outlined">
              Abbrechen
            </Button>
            <Button
              onClick={handleImportSubmit}
              variant="contained"
              color="primary"
              disabled={!selectedFiles.length}
            >
              Importieren
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default PaymentImportIndexPage;
